import React, { memo, useState } from "react"

import { useViewerStore } from "Pods/ThreeDStudio/Hooks/useViewerStore"
import { PROVIDER_LABELS } from "Pods/ThreeDStudio/Shared/AIEngineSelector/providerLabels"
import { useViewerContext } from "Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext"
import { useThreeDStudioStore } from "Pods/ThreeDStudio/Store"
import { MATERIAL, POLYGON_TYPE } from "Pods/ThreeDStudio/Store/constants"

import CardBody from "./Components/CardBody/CardBody"
import CompactCard from "./Components/CompactCard/CompactCard"
import { formatInteger } from "./Utils/formatInteger"

const TOPOLOGY_LABELS = {
  quad: 'Quad',
  triangle: 'Triangle',
}

const POLYGON_TYPE_LABELS = {
  [POLYGON_TYPE.TRIANGLE]: 'Triangle',
  [POLYGON_TYPE.QUADRILATERAL]: 'Quad',
}

const MATERIAL_LABELS = {
  [MATERIAL.PBR]: 'PBR',
  [MATERIAL.BASIC]: 'Basic',
  [MATERIAL.GEOMETRY_ONLY]: 'Geometry Only',
}

function ModelDetailsCard() {
  const viewer = useViewerContext()
  const stats = useViewerStore(viewer, "stats")
  const [isExpanded, setIsExpanded] = useState(false)

  const providerModelId = useThreeDStudioStore(state => state.ui?.openedModel?.providerModelId)
  const generationProvider = useThreeDStudioStore(state => state.schema?.generationProvider)
  const material = useThreeDStudioStore(state => state.modelSettings?.material)
  const targetPolycount = useThreeDStudioStore(state => state.modelSettings?.targetPolycount)
  const polygonType = useThreeDStudioStore(state => state.modelSettings?.polygonType)
  const isSmartLowPoly = useThreeDStudioStore(state => state.modelSettings?.isSmartLowPoly)

  if (!viewer || !stats) {
    return null
  }

  const { triangleCount, vertexCount, topologyMode } = stats

  // Settings rows only exist for opened library runs; uploaded models have no
  // generation metadata, so the card stays at the base three rows.
  const hasSettings = Boolean(providerModelId)

  const baseRows = [
    { label: 'Topology', value: TOPOLOGY_LABELS[topologyMode] || '—' },
    { label: 'Faces', value: formatInteger(triangleCount) },
    { label: 'Vertices', value: formatInteger(vertexCount) },
  ]

  const settingsRows = [
    { label: 'AI Engine', value: PROVIDER_LABELS[generationProvider] || '—' },
    { label: 'Low poly', value: isSmartLowPoly ? 'True' : 'False' },
    { label: 'Polycount', value: formatInteger(targetPolycount) },
    { label: 'Polygon Type', value: POLYGON_TYPE_LABELS[polygonType] || '—' },
    { label: 'Material', value: MATERIAL_LABELS[material] || '—' },
  ]

  const rows = hasSettings && isExpanded ? [...baseRows, ...settingsRows] : baseRows

  const handleToggle = () => setIsExpanded(prev => !prev)

  const cardBody = (
    <CardBody rows={rows} hasSettings={hasSettings} isExpanded={isExpanded} onToggle={handleToggle} />
  )

  return (
    <>
      <div className="model-details-card model-details-card--inline">
        {cardBody}
      </div>
      <CompactCard>{cardBody}</CompactCard>
    </>
  )
}

export default memo(ModelDetailsCard)
